<script setup>
const { t } = useI18n();
const props = defineProps({
    password: { type: String, default: '' },
    target: { type: [String, Object], required: true },
    minLength: { type: Number, default: 12 },
    pos: { type: String, default: 'bottom-left' }
});

const rules = computed(() => {
    const v = props.password ?? '';
    return {
        length: v.length >= props.minLength,
        upper: /[A-Z]/.test(v),
        lower: /[a-z]/.test(v),
        number: /\d/.test(v),
        symbol: /[^A-Za-z0-9]/.test(v)
    };
});

const passedCount = computed(() =>
    ['length', 'upper', 'lower', 'number', 'symbol'].reduce((c, k) => (rules.value[k] ? c + 1 : c), 0)
);

const strength = computed(() => {
    if (!props.password) return 'Low';
    if (passedCount.value <= 2) return 'Weak';
    if (passedCount.value === 3) return 'Medium';
    return 'Strong';
});

const strengthPercent = computed(() => (passedCount.value / 5) * 100);

const strengthBarClass = computed(() => {
    switch (strength.value) {
        case 'Weak':
            return 'uk-background-danger';
        case 'Medium':
            return 'uk-background-warning';
        case 'Strong':
            return 'uk-background-success';
        default:
            return 'uk-background-muted';
    }
});
</script>

<template>
    <div class="pw-hint">
        <div class="uk-text-bold uk-margin-xsmall">
            {{ t('Must have at least') }}
            <span class="uk-text-primary">{{ minLength }} {{ t('characters') }}</span>
        </div>

        <div class="uk-flex uk-flex-middle uk-margin-xsmall">
            <progress class="uk-progress uk-margin-remove-bottom uk-width-medium" :class="strengthBarClass"
                :value="strengthPercent" max="100" aria-label="Password strength" />
            <span class="uk-label uk-margin-small-left" :class="{
                'uk-label-danger': strength === t('Weak'),
                'uk-label-warning': strength === t('Medium'),
                'uk-label-success': strength === t('Strong'),
                'uk-label-muted': strength === t('Low')
            }">
                {{ t(strength) }}
            </span>
        </div>

        <div class="uk-text-meta uk-margin-xsmall">{{ t('It’s better to have:') }}</div>
        <ul class="uk-list uk-list-bullet uk-margin-xsmall-top uk-margin-remove-bottom">
            <li :class="{ 'uk-text-success': rules.upper }">
                {{ t('At least one uppercase letter (A-Z)') }}
            </li>
            <li :class="{ 'uk-text-success': rules.lower }">
                {{ t('At least one lowercase letter (a-z)') }}
            </li>
            <li :class="{ 'uk-text-success': rules.symbol }">
                {{ t('At least one special character') }}
            </li>
            <li :class="{ 'uk-text-success': rules.number }">
                {{ t('At least one number (0-9)') }}
            </li>
            <li :class="{ 'uk-text-success': rules.length }">
                {{ t('A longer password') }}
            </li>
        </ul>
    </div>
</template>